import { useState } from 'react';
import { LuLoader } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';
import { formatDateTime } from 'utils';
import { useNotesQuery } from 'views/household/HouseholdNotes/note-queries';
import { Toast } from 'widgets';

import { useAttachNoteToCallMutation } from './call-queries';

/** Picks one of the household's existing notes and attaches it to the call. */
export const AttachNoteToCallDialog = ({
    open,
    onOpenChange,
    householdGUID,
    zoomPhoneCallGUID,
    attachedNoteGUIDs,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    householdGUID: string;
    zoomPhoneCallGUID: string;
    attachedNoteGUIDs: string[];
}) => {
    const [selectedNoteGUID, setSelectedNoteGUID] = useState<string | null>(null);
    const notesQuery = useNotesQuery(householdGUID);
    const attachMutation = useAttachNoteToCallMutation(zoomPhoneCallGUID);

    const availableNotes = (notesQuery.data ?? []).filter((note) => !attachedNoteGUIDs.includes(note.noteGUID));

    const handleAttach = () => {
        if (!selectedNoteGUID) return;
        attachMutation.mutate(selectedNoteGUID, {
            onSuccess: () => {
                Toast.success('Note attached to call.');
                setSelectedNoteGUID(null);
                onOpenChange(false);
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Attach existing note</DialogTitle>
                    <DialogDescription>Select a note from this household to attach to the call.</DialogDescription>
                </DialogHeader>
                <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
                    {notesQuery.isLoading && (
                        <div className="text-muted-foreground flex items-center gap-2 text-sm">
                            <LuLoader className="h-4 w-4 animate-spin" />
                            <span>Loading notes…</span>
                        </div>
                    )}
                    {!notesQuery.isLoading && availableNotes.length === 0 && (
                        <div className="text-muted-foreground rounded-md border bg-gray-50 p-4 text-sm">
                            This household has no other notes to attach.
                        </div>
                    )}
                    {availableNotes.map((note) => (
                        <button
                            key={note.noteGUID}
                            onClick={() => setSelectedNoteGUID(note.noteGUID)}
                            className={cn(
                                'cursor-pointer rounded-lg border p-3 text-left transition',
                                selectedNoteGUID === note.noteGUID
                                    ? 'border-[#946A3E] bg-[#F8F7F5]'
                                    : 'border-border bg-white hover:border-gray-300',
                            )}
                        >
                            <div className="truncate text-sm font-medium">{note.subject || 'Untitled Note'}</div>
                            <div className="text-muted-foreground text-xs">{formatDateTime(note.createdAt)}</div>
                        </button>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleAttach} disabled={!selectedNoteGUID || attachMutation.isPending}>
                        {attachMutation.isPending && <LuLoader className="h-4 w-4 animate-spin" />}
                        Attach note
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
